import { useEffect, useState, useCallback } from 'react';
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Form, FormGroup, Label, Input, Button, FormText, Row, Col
} from 'reactstrap';
import { useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { FileUploader } from '../Components/FileUploader';
import { formatDateForInput } from '../Helpers/Utils';
import { moveFacilityItem } from '../Helpers/Api';
import { getRequestTypes } from '../../RMVendors/Helpers/Api';

const AddEditFacilityItemModal = ({
  mode,
  open,
  onClose,
  formik,
  propertyList,
  itemId,
  user
}) => {
  const dispatch = useDispatch();
  const [activeIndex, setActiveIndex] = useState(0);
  const [requestTypes, setRequestTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  const isAddMode = mode === 'add';
  const isEditMode = mode === 'edit';

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? '#007bff' : '#ced4da',
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : 'none',
      '&:hover': {
        borderColor: '#007bff',
      },
      minHeight: '38px',
      fontSize: '14px',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? '#007bff'
        : state.isFocused
          ? '#f8f9fa'
          : 'white',
      color: state.isSelected ? 'white' : '#495057',
      '&:hover': {
        backgroundColor: state.isSelected ? '#007bff' : '#e9ecef',
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#6c757d',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#495057',
    }),
  };

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'under_maintenance', label: 'Under Maintenance' },
    { value: 'retired', label: 'Retired' },
  ];

  const fetchRequestTypes = useCallback(async () => {
    if (!user?.id) {
      Swal.fire({
        icon: 'error',
        title: 'Authentication Error',
        text: 'User not authenticated. Please log in.',
      });
      return;
    }

    try {
      setLoading(true);
      const data = await getRequestTypes(user.company_id || user.id);
      setRequestTypes(data);
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: error.message });
      setRequestTypes([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (open && isAddMode) {
      setActiveIndex(0);
    }
    if (open) {
      fetchRequestTypes();
    }
  }, [open, isAddMode, fetchRequestTypes]);

  const handleAddItem = () => {
    const newItems = [
      ...(formik.values.items || []),
      {
        name: '',
        make: '',
        modelNo: '',
        requestsSupported: [],
        quantity: 1,
        costPerUnit: 0,
        warrantyDetails: [
          {
            boughtOn: undefined,
            boughtFrom: undefined,
            warrantyExpirationDate: '',
          },
        ],
        locationDetails: {
          room: '',
          notes: '',
        },
        images: [
          {
            url: '',
            uploadedBy: {
              id: user?.id || '',
              role: user?.role || '',
            },
          },
        ],
        invoices: [
          {
            url: '',
            uploadedBy: {
              id: user?.id || '',
              role: user?.role || '',
            },
          },
        ],
        status: 'active',
      },
    ];
    formik.setFieldValue('items', newItems);
    setActiveIndex(newItems.length - 1);
  };

  const handleRemoveItem = (index) => {
    const currentItems = formik.values.items || [];
    if (currentItems.length <= 1) return;

    const newItems = currentItems.filter((_, i) => i !== index);
    formik.setFieldValue('items', newItems);
    setActiveIndex(Math.min(activeIndex, newItems.length - 1));
  };

  const getFieldValue = (field, defaultValue = '') => {
    if (isAddMode) {
      return formik.values.items?.[activeIndex]?.[field] ?? defaultValue;
    }
    return formik.values.item?.[field] ?? defaultValue;
  };

  const getNestedFieldValue = (nestedField, field, defaultValue = '') => {
    if (isAddMode) {
      return formik.values.items?.[activeIndex]?.[nestedField]?.[field] ?? defaultValue;
    }
    return formik.values.item?.[nestedField]?.[field] ?? defaultValue;
  };

  const getWarrantyFieldValue = (index, field, defaultValue = '') => {
    if (isAddMode) {
      return formik.values.items?.[activeIndex]?.warrantyDetails?.[index]?.[field] ?? defaultValue;
    }
    return formik.values.item?.warrantyDetails?.[index]?.[field] ?? defaultValue;
  };

  const getImageFieldValue = (index, field, defaultValue = '') => {
    if (isAddMode) {
      return formik.values.items?.[activeIndex]?.images?.[index]?.[field] ?? defaultValue;
    }
    return formik.values.item?.images?.[index]?.[field] ?? defaultValue;
  };

  const getInvoiceFieldValue = (index, field, defaultValue = '') => {
    if (isAddMode) {
      return formik.values.items?.[activeIndex]?.invoices?.[index]?.[field] ?? defaultValue;
    }
    return formik.values.item?.invoices?.[index]?.[field] ?? defaultValue;
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    if (isAddMode) {
      formik.setFieldValue(`items[${activeIndex}].${name}`, value);
    } else {
      formik.setFieldValue(`item.${name}`, value);
    }
  };

  const handleRequestsSupportedChange = (selectedOptions) => {
    const values = selectedOptions ? selectedOptions.map(option => option.value) : [];
    if (isAddMode) {
      formik.setFieldValue(`items[${activeIndex}].requestsSupported`, values);
    } else {
      formik.setFieldValue(`item.requestsSupported`, values);
    }
  };

  const handleStatusChange = (selectedOption) => {
    const value = selectedOption ? selectedOption.value : '';
    if (isAddMode) {
      formik.setFieldValue(`items[${activeIndex}].status`, value);
    } else {
      formik.setFieldValue(`item.status`, value);
    }
  };

  const handleNestedFieldChange = (nestedField, field) => (e) => {
    const { value } = e.target;
    if (isAddMode) {
      formik.setFieldValue(`items[${activeIndex}].${nestedField}.${field}`, value);
    } else {
      formik.setFieldValue(`item.${nestedField}.${field}`, value);
    }
  };

  const handleWarrantyFieldChange = (index, field) => (e) => {
    const { value } = e.target;
    if (isAddMode) {
      formik.setFieldValue(`items[${activeIndex}].warrantyDetails[${index}].${field}`, value);
    } else {
      formik.setFieldValue(`item.warrantyDetails[${index}].${field}`, value);
    }
  };

  const handleTopLevelFieldChange = (e) => {
    const { name, value } = e.target;
    formik.setFieldValue(name, value);
  };

  const getFieldError = (field) => {
    if (isAddMode) {
      const itemErrors = formik.errors.items?.[activeIndex];
      const itemTouched = formik.touched.items?.[activeIndex];
      return itemTouched?.[field] && itemErrors?.[field] ? itemErrors[field] : null;
    }
    return formik.touched.item?.[field] && formik.errors.item?.[field] ? formik.errors.item[field] : null;
  };

  const getNestedFieldError = (nestedField, field) => {
    if (isAddMode) {
      const itemErrors = formik.errors.items?.[activeIndex]?.[nestedField];
      const itemTouched = formik.touched.items?.[activeIndex]?.[nestedField];
      return itemTouched?.[field] && itemErrors?.[field] ? itemErrors[field] : null;
    }
    return formik.touched.item?.[nestedField]?.[field] && formik.errors.item?.[nestedField]?.[field]
      ? formik.errors.item[nestedField][field] : null;
  };

  const getWarrantyFieldError = (index, field) => {
    if (isAddMode) {
      const warrantyErrors = formik.errors.items?.[activeIndex]?.warrantyDetails?.[index];
      const warrantyTouched = formik.touched.items?.[activeIndex]?.warrantyDetails?.[index];
      return warrantyTouched?.[field] && warrantyErrors?.[field] ? warrantyErrors[field] : null;
    }
    return formik.touched.item?.warrantyDetails?.[index]?.[field] && formik.errors.item?.warrantyDetails?.[index]?.[field]
      ? formik.errors.item.warrantyDetails[index][field] : null;
  };

  const getTopLevelFieldError = (field) => {
    return formik.touched[field] && formik.errors[field] ? formik.errors[field] : null;
  };

  const handleFieldBlur = (e) => {
    const { name } = e.target;
    if (isAddMode) {
      formik.setFieldTouched(`items[${activeIndex}].${name}`, true);
    } else {
      formik.setFieldTouched(`item.${name}`, true);
    }
  };

  const handleRequestsSupportedBlur = () => {
    if (isAddMode) {
      formik.setFieldTouched(`items[${activeIndex}].requestsSupported`, true);
    } else {
      formik.setFieldTouched(`item.requestsSupported`, true);
    }
  };

  const handleStatusBlur = () => {
    if (isAddMode) {
      formik.setFieldTouched(`items[${activeIndex}].status`, true);
    } else {
      formik.setFieldTouched(`item.status`, true);
    }
  };

  const handleNestedFieldBlur = (nestedField, field) => () => {
    if (isAddMode) {
      formik.setFieldTouched(`items[${activeIndex}].${nestedField}.${field}`, true);
    } else {
      formik.setFieldTouched(`item.${nestedField}.${field}`, true);
    }
  };

  const handleWarrantyFieldBlur = (index, field) => () => {
    if (isAddMode) {
      formik.setFieldTouched(`items[${activeIndex}].warrantyDetails[${index}].${field}`, true);
    } else {
      formik.setFieldTouched(`item.warrantyDetails[${index}].${field}`, true);
    }
  };

  const handleTopLevelFieldBlur = (e) => {
    const { name } = e.target;
    formik.setFieldTouched(name, true);
  };

  const handleAddWarranty = () => {
    const newWarranty = {
      boughtOn: undefined,
      boughtFrom: undefined,
      warrantyExpirationDate: '',
    };
    if (isAddMode) {
      const currentWarranties = formik.values.items?.[activeIndex]?.warrantyDetails || [];
      formik.setFieldValue(`items[${activeIndex}].warrantyDetails`, [...currentWarranties, newWarranty]);
    } else {
      const currentWarranties = formik.values.item?.warrantyDetails || [];
      formik.setFieldValue('item.warrantyDetails', [...currentWarranties, newWarranty]);
    }
  };

  const handleRemoveWarranty = (index) => {
    if (isAddMode) {
      const currentWarranties = formik.values.items?.[activeIndex]?.warrantyDetails || [];
      formik.setFieldValue(
        `items[${activeIndex}].warrantyDetails`,
        currentWarranties.filter((_, i) => i !== index)
      );
    } else {
      const currentWarranties = formik.values.item?.warrantyDetails || [];
      formik.setFieldValue(
        'item.warrantyDetails',
        currentWarranties.filter((_, i) => i !== index)
      );
    }
  };

  const handleAddImage = () => {
    const newImage = {
      url: '',
      uploadedBy: {
        id: user?.id || '',
        role: user?.role || '',
      },
    };
    if (isAddMode) {
      const currentImages = formik.values.items?.[activeIndex]?.images || [];
      formik.setFieldValue(`items[${activeIndex}].images`, [...currentImages, newImage]);
    } else {
      const currentImages = formik.values.item?.images || [];
      formik.setFieldValue('item.images', [...currentImages, newImage]);
    }
  };

  const handleRemoveImage = (index) => {
    if (isAddMode) {
      const currentImages = formik.values.items?.[activeIndex]?.images || [];
      formik.setFieldValue(
        `items[${activeIndex}].images`,
        currentImages.filter((_, i) => i !== index)
      );
    } else {
      const currentImages = formik.values.item?.images || [];
      formik.setFieldValue(
        'item.images',
        currentImages.filter((_, i) => i !== index)
      );
    }
  };

  const handleAddInvoice = () => {
    const newInvoice = {
      url: '',
      uploadedBy: {
        id: user?.id || '',
        role: user?.role || '',
      },
    };
    if (isAddMode) {
      const currentInvoices = formik.values.items?.[activeIndex]?.invoices || [];
      formik.setFieldValue(`items[${activeIndex}].invoices`, [...currentInvoices, newInvoice]);
    } else {
      const currentInvoices = formik.values.item?.invoices || [];
      formik.setFieldValue('item.invoices', [...currentInvoices, newInvoice]);
    }
  };

  const handleRemoveInvoice = (index) => {
    if (isAddMode) {
      const currentInvoices = formik.values.items?.[activeIndex]?.invoices || [];
      formik.setFieldValue(
        `items[${activeIndex}].invoices`,
        currentInvoices.filter((_, i) => i !== index)
      );
    } else {
      const currentInvoices = formik.values.item?.invoices || [];
      formik.setFieldValue(
        'item.invoices',
        currentInvoices.filter((_, i) => i !== index)
      );
    }
  };

  const handleMoveItem = async () => {
    if (!isEditMode || !itemId) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Move functionality is only available in edit mode with a valid item ID.',
      });
      return;
    }

    if (!user?.id) {
      Swal.fire({
        icon: 'error',
        title: 'Authentication Error',
        text: 'User not authenticated. Please log in.',
      });
      return;
    }

    const currentQuantity = formik.values.item?.quantity || 1;
    const propertyOptions = propertyList.map(prop => ({
      value: prop._id,
      label: `${prop.title}, ${prop.location}`,
    }));

    let selectedPropertyId = '';
    let selectedBuildingName = '';
    let buildingOptions = [];

    const updateBuildingOptions = (propertyId) => {
      const selectedProperty = propertyList.find(prop => prop._id === propertyId);
      return selectedProperty?.building
        ?.filter(b => b && b.buildingName)
        ?.map(b => ({
          value: b.buildingName,
          label: b.buildingName,
        })) || [];
    };

    await Swal.fire({
      title: 'Move Facility Item',
      width: '700px',
      html: `
      <div>
        <label for="destination_property_id" style="display: block; margin-bottom: 5px;">Destination Property *</label>
        <select id="destination_property_id" class="swal2-select" style="width: 80%; padding: 8px;">
          <option value="">Select Property</option>
          ${propertyOptions.map(opt => `<option value="${opt.value}">${opt.label}</option>`).join('')}
        </select>
        <label for="destination_building_name" style="display: block; margin-top: 15px; margin-bottom: 5px;">Building Name *</label>
        <select id="destination_building_name" class="swal2-select" style="width: 80%; padding: 8px;" disabled>
          <option value="">Select Building</option>
        </select>
        <label for="quantity" style="display: block; margin-top: 15px; margin-bottom: 5px;">Quantity (Max: ${currentQuantity})</label>
        <input type="number" id="movable_quantity" class="swal2-input" placeholder="Enter quantity" min="1" max="${currentQuantity}" style="width: 80%;">
      </div>
    `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Move',
      preConfirm: () => {
        const propertyId = document.getElementById('destination_property_id').value;
        const buildingName = document.getElementById('destination_building_name').value;
        const quantity = document.getElementById('movable_quantity').value;

        const quantityNum = parseInt(quantity, 10);

        if (!propertyId) {
          Swal.showValidationMessage('Please select a destination property');
          return false;
        }
        if (!buildingName) {
          Swal.showValidationMessage('Please select a building name');
          return false;
        }

        if (!quantity || quantity.trim() === '') {
          Swal.showValidationMessage('Please enter a quantity');
          return false;
        }

        if (isNaN(quantityNum)) {
          Swal.showValidationMessage('Quantity must be a valid number');
          return false;
        }

        if (quantityNum <= 0) {
          Swal.showValidationMessage('Quantity must be greater than 0');
          return false;
        }

        if (quantityNum > currentQuantity) {
          Swal.showValidationMessage(`Quantity cannot exceed ${currentQuantity}`);
          return false;
        }

        return {
          item_id: itemId,
          destination_property_id: propertyId,
          destination_building_name: buildingName,
          quantity: quantityNum,
        };
      },
      didOpen: () => {
        const propertySelect = document.getElementById('destination_property_id');
        const buildingSelect = document.getElementById('destination_building_name');
        const quantityInput = document.getElementById('movable_quantity');

        quantityInput.addEventListener('input', (e) => {
          let val = parseInt(e.target.value, 10);
          if (val > currentQuantity) e.target.value = currentQuantity;
          if (val < 1) e.target.value = 1;
        });

        propertySelect.addEventListener('change', (e) => {
          selectedPropertyId = e.target.value;
          buildingOptions = updateBuildingOptions(selectedPropertyId);
          buildingSelect.disabled = buildingOptions.length === 0;
          buildingSelect.innerHTML = `
          <option value="">Select Building</option>
          ${buildingOptions.map(opt => `<option value="${opt.value}">${opt.label}</option>`).join('')}
        `;
          if (buildingOptions.length === 1) {
            buildingSelect.value = buildingOptions[0].value;
            selectedBuildingName = buildingOptions[0].value;
          }
        });

        buildingSelect.addEventListener('change', (e) => {
          selectedBuildingName = e.target.value;
        });
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          dispatch({ type: 'SET_LOADING', payload: true });
          const response = await moveFacilityItem(result.value);
          Swal.fire({
            icon: 'success',
            title: 'Success',
            text: response.message || 'Facility item moved successfully',
          });
          onClose();
        } catch (error) {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error.message || 'Failed to move facility item',
          });
        } finally {
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      }
    });
  };

  const formatRequestTypesForSelect = (requestTypesArray) => {
    return requestTypesArray?.map(type => ({
      value: type._id,
      label: type.request_type
    })) || [];
  };

  const getRequestsSupportedValue = () => {
    const value = getFieldValue('requestsSupported', []);
    return formatRequestTypesForSelect(requestTypes).filter(option => value.includes(option.value));
  };

  const getStatusValue = () => {
    const value = getFieldValue('status', 'active');
    return statusOptions.find(option => option.value === value) || null;
  };

  const count = isAddMode ? (formik.values.items?.length ?? 0) : 0;
  const warrantyDetails = isAddMode
    ? formik.values.items?.[activeIndex]?.warrantyDetails || []
    : formik.values.item?.warrantyDetails || [];
  const images = isAddMode
    ? formik.values.items?.[activeIndex]?.images || []
    : formik.values.item?.images || [];
  const invoices = isAddMode
    ? formik.values.items?.[activeIndex]?.invoices || []
    : formik.values.item?.invoices || [];

  return (
    <Modal isOpen={open} toggle={onClose} size="xl">
      <Form onSubmit={formik.handleSubmit}>
        <ModalHeader toggle={onClose}>
          <div className="d-flex justify-content-between align-items-center">
            <span>
              {isEditMode ? 'Edit Facility Item' : 'Add Facility Item'}
              {isAddMode && count > 1 && ` (${count})`}
            </span>
            {isEditMode && (
              <Button
                type="button"
                color="warning"
                size="sm"
                onClick={handleMoveItem}
                disabled={formik.isSubmitting || !user?.id}
                className="ms-3"
              >
                Move Item
              </Button>
            )}
          </div>
        </ModalHeader>

        {isAddMode && count > 1 && (
          <div className="px-3 pt-2 border-bottom">
            <Row className="mb-2">
              <Col>
                <div className="d-flex flex-nowrap overflow-auto" style={{ gap: '8px' }}>
                  {formik.values.items.map((_, index) => (
                    <Button
                      key={index}
                      type="button"
                      color={activeIndex === index ? 'primary' : 'secondary'}
                      outline={activeIndex !== index}
                      size="sm"
                      onClick={() => setActiveIndex(index)}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      Item {index + 1}
                    </Button>
                  ))}
                </div>
              </Col>
            </Row>
            <Row className="mb-2">
              <Col>
                <Button
                  type="button"
                  color="success"
                  size="sm"
                  onClick={handleAddItem}
                  className="me-2"
                  disabled={formik.isSubmitting}
                >
                  Add Item
                </Button>
                {count > 1 && (
                  <Button
                    type="button"
                    color="danger"
                    size="sm"
                    onClick={() => handleRemoveItem(activeIndex)}
                    disabled={formik.isSubmitting}
                  >
                    Remove Current
                  </Button>
                )}
              </Col>
            </Row>
          </div>
        )}

        <ModalBody>
          <Row className="mb-3">
            <Col md={3}>
              <FormGroup>
                <Label for="name" style={{ fontSize: '0.875rem' }}>Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={getFieldValue('name')}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  disabled={formik.isSubmitting}
                  invalid={!!getFieldError('name')}
                  required
                />
                {getFieldError('name') && (
                  <FormText color="danger">{getFieldError('name')}</FormText>
                )}
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="make" style={{ fontSize: '0.875rem' }}>Make</Label>
                <Input
                  id="make"
                  name="make"
                  value={getFieldValue('make')}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  disabled={formik.isSubmitting}
                  invalid={!!getFieldError('make')}
                />
                {getFieldError('make') && (
                  <FormText color="danger">{getFieldError('make')}</FormText>
                )}
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="modelNo" style={{ fontSize: '0.875rem' }}>Model Number</Label>
                <Input
                  id="modelNo"
                  name="modelNo"
                  value={getFieldValue('modelNo')}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  disabled={formik.isSubmitting}
                  invalid={!!getFieldError('modelNo')}
                />
                {getFieldError('modelNo') && (
                  <FormText color="danger">{getFieldError('modelNo')}</FormText>
                )}
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="requestsSupported" style={{ fontSize: '0.875rem' }}>Request Types *</Label>
                <Select
                  id="requestsSupported"
                  name="requestsSupported"
                  options={formatRequestTypesForSelect(requestTypes)}
                  value={getRequestsSupportedValue()}
                  onChange={handleRequestsSupportedChange}
                  onBlur={handleRequestsSupportedBlur}
                  isMulti
                  isClearable
                  isSearchable
                  styles={selectStyles}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isDisabled={formik.isSubmitting || loading}
                  isLoading={loading}
                  placeholder="Select Request Types..."
                  required
                />
                {getFieldError('requestsSupported') && (
                  <FormText color="danger">{getFieldError('requestsSupported')}</FormText>
                )}
              </FormGroup>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={1}>
              <FormGroup>
                <Label for="quantity" style={{ fontSize: '0.875rem' }}>Quantity *</Label>
                <Input
                  id="quantity"
                  name="quantity"
                  type="number"
                  min="0"
                  value={getFieldValue('quantity', 0)}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  disabled={formik.isSubmitting}
                  invalid={!!getFieldError('quantity')}
                  required
                />
                {getFieldError('quantity') && (
                  <FormText color="danger">{getFieldError('quantity')}</FormText>
                )}
              </FormGroup>
            </Col>
            <Col md={2}>
              <FormGroup>
                <Label for="costPerUnit" style={{ fontSize: '0.875rem' }}>Cost Per Unit *</Label>
                <Input
                  id="costPerUnit"
                  name="costPerUnit"
                  type="number"
                  min="0"
                  step="0.01"
                  value={getFieldValue('costPerUnit', 0)}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  disabled={formik.isSubmitting}
                  invalid={!!getFieldError('costPerUnit')}
                  required
                />
                {getFieldError('costPerUnit') && (
                  <FormText color="danger">{getFieldError('costPerUnit')}</FormText>
                )}
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="locationDetails.room" style={{ fontSize: '0.875rem' }}>Location - Room</Label>
                <Input
                  name="room"
                  value={getNestedFieldValue('locationDetails', 'room')}
                  onChange={handleNestedFieldChange('locationDetails', 'room')}
                  onBlur={handleNestedFieldBlur('locationDetails', 'room')}
                  disabled={formik.isSubmitting}
                  invalid={!!getNestedFieldError('locationDetails', 'room')}
                />
                {getNestedFieldError('locationDetails', 'room') && (
                  <FormText color="danger">{getNestedFieldError('locationDetails', 'room')}</FormText>
                )}
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="locationDetails.notes" style={{ fontSize: '0.875rem' }}>Notes</Label>
                <Input
                  name="notes"
                  type="textarea"
                  value={getNestedFieldValue('locationDetails', 'notes')}
                  onChange={handleNestedFieldChange('locationDetails', 'notes')}
                  onBlur={handleNestedFieldBlur('locationDetails', 'notes')}
                  disabled={formik.isSubmitting}
                  rows={1}
                />
                {getNestedFieldError('locationDetails', 'notes') && (
                  <FormText color="danger">{getNestedFieldError('locationDetails', 'notes')}</FormText>
                )}
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="status" style={{ fontSize: '0.875rem' }}>Status</Label>
                <Select
                  id="status"
                  name="status"
                  options={statusOptions}
                  value={getStatusValue()}
                  onChange={handleStatusChange}
                  onBlur={handleStatusBlur}
                  isClearable
                  isSearchable
                  styles={selectStyles}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isDisabled={formik.isSubmitting}
                  placeholder="Select Status..."
                />
                {getFieldError('status') && (
                  <FormText color="danger">{getFieldError('status')}</FormText>
                )}
              </FormGroup>
            </Col>
          </Row>

          <div className="mb-3">
            {warrantyDetails.map((_, index) => (
              <div key={index} className="border p-3 mb-3 rounded">
                <Row className="mb-2">
                  <Col>
                    <h6 style={{ fontSize: '0.875rem' }}>Warranty Details {index + 1}</h6>
                  </Col>
                  <Col xs="auto">
                    <Button
                      type="button"
                      color="success"
                      size="sm"
                      className='me-2'
                      onClick={handleAddWarranty}
                      disabled={formik.isSubmitting}
                    >
                      Add Warranty
                    </Button>
                    <Button
                      color="danger"
                      size="sm"
                      onClick={() => handleRemoveWarranty(index)}
                      disabled={formik.isSubmitting}
                    >
                      Remove Warranty
                    </Button>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={4}>
                    <FormGroup>
                      <Label style={{ fontSize: '0.875rem' }}>Bought On</Label>
                      <Input
                        type="date"
                        value={formatDateForInput(getWarrantyFieldValue(index, 'boughtOn'))}
                        onChange={handleWarrantyFieldChange(index, 'boughtOn')}
                        onBlur={handleWarrantyFieldBlur(index, 'boughtOn')}
                        disabled={formik.isSubmitting}
                        invalid={!!getWarrantyFieldError(index, 'boughtOn')}
                      />
                      {getWarrantyFieldError(index, 'boughtOn') && (
                        <FormText color="danger">{getWarrantyFieldError(index, 'boughtOn')}</FormText>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label style={{ fontSize: '0.875rem' }}>Bought From</Label>
                      <Input
                        value={getWarrantyFieldValue(index, 'boughtFrom')}
                        onChange={handleWarrantyFieldChange(index, 'boughtFrom')}
                        onBlur={handleWarrantyFieldBlur(index, 'boughtFrom')}
                        disabled={formik.isSubmitting}
                        invalid={!!getWarrantyFieldError(index, 'boughtFrom')}
                      />
                      {getWarrantyFieldError(index, 'boughtFrom') && (
                        <FormText color="danger">{getWarrantyFieldError(index, 'boughtFrom')}</FormText>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label style={{ fontSize: '0.875rem' }}>Warranty Expiration Date</Label>
                      <Input
                        type="date"
                        value={formatDateForInput(getWarrantyFieldValue(index, 'warrantyExpirationDate'))}
                        onChange={handleWarrantyFieldChange(index, 'warrantyExpirationDate')}
                        onBlur={handleWarrantyFieldBlur(index, 'warrantyExpirationDate')}
                        disabled={formik.isSubmitting}
                        invalid={!!getWarrantyFieldError(index, 'warrantyExpirationDate')}
                      />
                      {getWarrantyFieldError(index, 'warrantyExpirationDate') && (
                        <FormText color="danger">{getWarrantyFieldError(index, 'warrantyExpirationDate')}</FormText>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
              </div>
            ))}
            {warrantyDetails.length === 0 && (
              <Button
                type="button"
                color="success"
                size="sm"
                onClick={handleAddWarranty}
                disabled={formik.isSubmitting}
              >
                Add Warranty
              </Button>
            )}
          </div>

          <Row className="mb-3">
            <Col md={6}>
              {images.map((_, index) => (
                <div key={index} className="border p-3 mb-3 rounded">
                  <Row className="mb-2">
                    <Col>
                      <h6 style={{ fontSize: '0.875rem' }}>Image {index + 1}</h6>
                    </Col>
                    <Col xs="auto">
                      <Button
                        type="button"
                        color="success"
                        size="sm"
                        className='me-2'
                        onClick={handleAddImage}
                        disabled={formik.isSubmitting}
                      >
                        Add Image
                      </Button>
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => handleRemoveImage(index)}
                        disabled={formik.isSubmitting}
                      >
                        Remove Image
                      </Button>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col>
                      <FileUploader
                        formik={formik}
                        fieldName={isAddMode ? `items[${activeIndex}].images[${index}].url` : `item.images[${index}].url`}
                        label="Image File"
                        isDisabled={formik.isSubmitting}
                        acceptedFileTypes="image/jpeg,image/png,image/gif,image/webp"
                      />
                    </Col>
                  </Row>
                </div>
              ))}
              {images.length === 0 && (
                <Button
                  type="button"
                  color="success"
                  size="sm"
                  onClick={handleAddImage}
                  disabled={formik.isSubmitting}
                >
                  Add Image
                </Button>
              )}
            </Col>

            <Col md={6}>
              {invoices.map((_, index) => (
                <div key={index} className="border p-3 mb-3 rounded">
                  <Row className="mb-2">
                    <Col>
                      <h6 style={{ fontSize: '0.875rem' }}>Invoice {index + 1}</h6>
                    </Col>
                    <Col xs="auto">
                      <Button
                        type="button"
                        color="success"
                        size="sm"
                        className='me-2'
                        onClick={handleAddInvoice}
                        disabled={formik.isSubmitting}
                      >
                        Add Invoice
                      </Button>
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => handleRemoveInvoice(index)}
                        disabled={formik.isSubmitting}
                      >
                        Remove Invoice
                      </Button>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col>
                      <FileUploader
                        formik={formik}
                        fieldName={isAddMode ? `items[${activeIndex}].invoices[${index}].url` : `item.invoices[${index}].url`}
                        label="Invoice File"
                        isDisabled={formik.isSubmitting}
                        acceptedFileTypes="image/jpeg,image/png,application/pdf"
                      />
                    </Col>
                  </Row>
                </div>
              ))}
              {invoices.length === 0 && (
                <Button
                  type="button"
                  color="success"
                  size="sm"
                  onClick={handleAddInvoice}
                  disabled={formik.isSubmitting}
                >
                  Add Invoice
                </Button>
              )}
            </Col>
          </Row>
        </ModalBody>

        <ModalFooter>
          <Button
            type="submit"
            color="primary"
            disabled={formik.isSubmitting || !user?.id}
          >
            {isEditMode ? 'Update' : 'Save'}
          </Button>
          <Button
            type="button"
            color="secondary"
            onClick={onClose}
            disabled={formik.isSubmitting}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Form>

      <style jsx>{`
        .react-select-container .react-select__control {
          border-radius: 0.375rem;
          transition: all 0.15s ease-in-out;
        }
        
        .react-select-container .react-select__control:hover {
          border-color: #007bff;
        }
        
        .react-select-container .react-select__menu {
          border-radius: 0.375rem;
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
        }
      `}</style>
    </Modal>
  );
};

export { AddEditFacilityItemModal };